import { resetFilterRequest } from '../api/filter.js';
import { COLLECTION_ALL_STORAGE_KEY, FILTER_OPTION_SELECTOR } from '../constants/filter-tree.js';
import { getTemplate } from '../template';
import {
  appendQueryParamHistory,
  debounce,
  debug,
  formatFromToKey,
  formatPercentSaleLabel,
  formatPriceLabel,
  getLimitOptionValues,
  getLocalStorage,
  getUrlScheme,
  isMobile,
  isViewMoreFilterOption,
  manageFilterTreeViewMoreState,
  replacePrefixValue,
  scrollToTop,
  setQueryParamHistory,
  simpleMd5,
  xssFilterOption,
} from '../utils';
import {
  clearFilter,
  getFilterSettings,
  getFilterTreeParent,
  getSelectedKeysFilter,
} from './index.js';
import { addFilterBoxSelectedClass, removeFilterBoxSelectedClass } from './option-box.js';
import { optionCollection } from './option-collection.js';
import { addFilterSwatchSelectedClass, removeFilterSwatchSelectedClass } from './option-swatch.js';

const SELECTED_CLASS = 'boost-sd__filter-option-item-button--selected';

/**
 * Renders the option list for a filter option.
 * Takes in context, dom element to render to, the filter option,
 * and an optional limit on the number of values to render initially.
 * Renders the option values as a list of buttons
 * with metadata for selecting that option.
 * @type {import('./index.d').renderOptionList}
 */
export async function renderOptionList(context, dom, option, limit) {
  if (!dom) return;
  if (option.values.length === 0) return;

  const selectedKeys = getSelectedKeysFilter(context);
  if (context.app.generalSettings.collection_id) {
    selectedKeys[`${option.filterOptionId}-${context.app.generalSettings.collection_id}`] = true;
  }

  const classNameUnselected = `boost-sd__filter-option-item-button boost-sd__filter-option-item-button--as-button ${
    option.selectType === 'multiple' ? 'boost-sd__filter-option-item-button--with-checkbox' : ''
  }`;
  const classNameSelected = classNameUnselected + ' boost-sd__filter-option-item-button--selected';

  const size = limit && option.values.length > limit ? limit : option.values.length;

  const [getActionMapping, setActionMapping] = context.useContextState('actionMapping', {});
  const actionMapping = getActionMapping() || {};
  const isXssDisabled = context.app?.xss?.filter?.disabled;
  let values = isXssDisabled ? getLimitOptionValues(option, size) : xssFilterOption(option, size);
  const label = option.label;

  // export customize boostCustomSortValues
  if (typeof window.boostCustomSortOptionValues === 'function') {
    values = window.boostCustomSortOptionValues(option, size);
  }

  const { showFilterOptionCount } = getFilterSettings(context);
  const showDocCount = showFilterOptionCount;

  if (option.filterType === 'collection') {
    // if enable settings show All in collection option
    if (option.activeCollectionAll) {
      const getIdCollectionAll = getLocalStorage(COLLECTION_ALL_STORAGE_KEY) || 0;
      const valueCollectionAll = window.boostSdCustomCollectionALl || {
        key: getIdCollectionAll,
        label: 'All',
        displayName: 'All',
        handle: 'all',
      };

      values?.unshift(valueCollectionAll);
    }
  }

  const params = {
    context,
    label,
    values,
    option,
    dom,
    showDocCount,
    selectedKeys,
    classNameSelected,
    classNameUnselected,
    actionMapping,
    setActionMapping,
  };

  await manageFilterTreeViewMoreState(params);
}

/**
 * @type {import('./index.d').renderingOptionListTemplate}
 */
export async function renderingOptionListTemplate({
  context,
  option,
  values,
  dom,
  label,
  showDocCount,
  selectedKeys,
  classNameSelected,
  classNameUnselected,
  actionMapping,
  setActionMapping,
}) {
  const { precisionFormatLabelPriceList } = getFilterSettings(context);
  let html = '';

  for (const value of values) {
    let className = classNameUnselected;

    if (['percent_sale', 'price', 'variants_price'].includes(option.filterType)) {
      if (!value.from) value.from = 0;

      value.key = formatFromToKey(value.from, value.to);
    }

    const itemKey = `${option.filterOptionId}-${value.key?.toString()}`?.toLowerCase();

    if (selectedKeys[itemKey]) {
      className = classNameSelected;
    }

    if (!value.doc_count) {
      value.doc_count = 0;
    }

    value.label = replacePrefixValue(option, value);

    if (['price', 'variants_price'].includes(option.filterType)) {
      value.label = formatPriceLabel(context, value.to, value.from, precisionFormatLabelPriceList);
    }

    if (option.filterType === 'percent_sale') {
      value.label = formatPercentSaleLabel(context, value.from, value.to);
    }

    const actionId = simpleMd5(`${option.filterOptionId}-${value.key}`);
    const dataAction = `${option.filterOptionId}.${actionId}`;

    const action = {
      optionList: {
        key: option.filterOptionId,
        value: value.key,
        label: option.label,
        filterType: option.filterType,
        selectType: option.selectType,
        displayType: option.displayType,
        valueDisplay: value.label || '',
        handle: value.handle || '',
      },
    };

    // assign mapping event
    actionMapping[option.filterOptionId] = actionMapping[option.filterOptionId] || {};
    actionMapping[option.filterOptionId][actionId] = action;

    html += await context.templateRender(getTemplate(context).filterOptionListTemplate, {
      actionId,
      dataAction,
      value,
      label,
      className,
      showDocCount: value.handle === 'all' ? false : showDocCount,
      displayAllValuesInUppercaseForm: option.displayAllValuesInUppercaseForm,
    });
  }

  setActionMapping(actionMapping);
  dom.innerHTML = html;

  // create custom events - after render
  const renderFiltered = new CustomEvent('afterRenderHtmlForFilter');
  window.dispatchEvent(renderFiltered);
}

/**
 * @type {import('./index.d').optionList}
 */
export function optionList(context, action) {
  const { filterLayout, requestInstantly } = getFilterSettings(context);
  let isCalled = true;

  // option list but filterType = collection => use optionCollection
  if (action?.filterType === 'collection') {
    return optionCollection(context, action);
  }

  const horizontalHasApplyButton =
    filterLayout === 'horizontal' &&
    !requestInstantly &&
    !isMobile(context.app.generalSettings.isTabletPortraitMax);

  if (horizontalHasApplyButton) {
    const [getItemSelecting, setItemSelecting] = context.useContextState(
      'filterOptionItemSelecting',
      {}
    );

    const _getItemSelecting = getItemSelecting();

    if (!_getItemSelecting[`${action.key}-${action.value}`]) {
      _getItemSelecting[`${action.key}-${action.value}`] = true;
    } else {
      delete _getItemSelecting[`${action.key}-${action.value}`];
    }

    setItemSelecting(_getItemSelecting);
    isCalled = false;
  }

  isCalled && scrollToTop({ isFilter: true });

  const { key, value, selectType } = action;
  const [getFilter] = context.useContextState('filter', []);
  const filterTree = getFilter();

  if (filterTree.some((filter) => filter?.data?.key === key && filter?.data?.value === value)) {
    clearFilter(context, action);
  } else {
    if (selectType === 'multiple') {
      addOptionList(context, action);
    } else {
      setOptionList(context, action);
    }
  }

  isCalled && resetFilterRequest(context);

  renderSelectedOption(context);
}

/**
 * Add more option selected to filter
 * @type {import('./index.d').addOptionList}
 */
const addOptionList = (context, action) => {
  const { key, value, displayType } = action;
  const [getFilter, setFilter] = context.useContextState('filter', []);
  const filterTree = getFilter();
  const { isShortenUrlParam } = getFilterSettings(context);

  addFilterSelectedClass(context, key, value, displayType);
  const newFilterTree = [...filterTree, generateFilterDataForList(context, key, value, action)];

  setFilter(newFilterTree);
  appendQueryParamHistory(key, value, false, {
    isShortenUrlParam,
    urlScheme: getUrlScheme(),
  });
};

/**
 * Replace option selected in filter, use for single selection filter
 * @type {import('./index.d').setOptionList}
 */
const setOptionList = (context, action) => {
  const { key, value, displayType } = action;
  const [getFilter, setFilter] = context.useContextState('filter', []);
  const filterTree = getFilter();
  filterTree
    .filter((filter) => filter.data.key === key)
    .forEach((filter) => {
      removeFilterSelectedClass(context, filter.data.key, filter.data.value, displayType);
    });
  const newFilterTree = filterTree.filter((filter) => filter.data.key !== key);
  const { isShortenUrlParam } = getFilterSettings(context);
  addFilterSelectedClass(context, key, value, displayType);
  newFilterTree.push(generateFilterDataForList(context, key, value, action));

  setFilter(newFilterTree);
  setQueryParamHistory(key, value, false, { isShortenUrlParam, urlScheme: getUrlScheme() });
};

/**
 * Lazily loads more option values as the user scrolls.
 * Uses the filterTreeViewPort context to track loaded vs total options.
 * Adds mousemove listener to load more when scrolled into view.
 * @type {import('./index.d').lazyLoadOptionList}
 */
export function lazyLoadOptionList(context, dom, id, option) {
  // Do not lazy load for View More options
  const shouldPrevent = isViewMoreFilterOption(option);

  if (shouldPrevent) return;

  const domMouseMove = dom?.closest(FILTER_OPTION_SELECTOR);

  const mousemoveRender = debounce(() => {
    debug(context);
    renderFullOptionList(context, dom, id, option);

    domMouseMove.removeEventListener('mousemove', mousemoveRender);
    domMouseMove.removeEventListener('touchstart', mousemoveRender, { passive: true });
  }, 200);

  domMouseMove && domMouseMove.addEventListener('mousemove', mousemoveRender);
  domMouseMove && domMouseMove.addEventListener('touchstart', mousemoveRender, { passive: true });
}

/**
 * Render full option list
 * @type {import('./index.d').renderFullOptionList}
 */
export const renderFullOptionList = (context, dom, id, option) => {
  if (
    context.filterTreeViewPort &&
    context.filterTreeViewPort[id] &&
    context.filterTreeViewPort[id].loaded < context.filterTreeViewPort[id].total &&
    context.filterTreeViewPort[id].displayType === 'list'
  ) {
    renderOptionList(context, dom, option, context.filterTreeViewPort[id].total);
    context.filterTreeViewPort[id] = {
      loaded: context.filterTreeViewPort[id].total,
      total: option.values.length,
      displayType: 'list',
    };
  }
};

/**
 * Renders the selected options in the filter tree UI.
 * finds options with "selected" status, and adds a selected class to their DOM elements.
 * @type {import('./index.d').renderSelectedOption}
 */
export function renderSelectedOption(context) {
  const [getFilter] = context.useContextState('filter', []);
  const selectedFilters = getFilter() || [];

  selectedFilters
    .filter((selected) => selected && selected.data && selected.data.key)
    .forEach((selected) =>
      addFilterSelectedClass(
        context,
        selected.data.key,
        selected.data.value,
        selected.metaData?.type
      )
    );
}

/**
 * @type {import('./index.d').addFilterSelectedClass}
 */
export const addFilterSelectedClass = (context, key, value, displayType = 'list') => {
  if (displayType === 'box') {
    return addFilterBoxSelectedClass(context, key, value);
  }
  if (displayType === 'swatch') {
    return addFilterSwatchSelectedClass(context, key, value);
  }

  addFilterListSelectedClass(context, key, value);
};

/**
 * @type {import('./index.d').removeFilterSelectedClass}
 */
export const removeFilterSelectedClass = (context, key, value, displayType) => {
  if (displayType === 'box') {
    return removeFilterBoxSelectedClass(context, key, value);
  }
  if (displayType === 'swatch') {
    return removeFilterSwatchSelectedClass(context, key, value);
  }

  removeFilterListSelectedClass(context, key, value);
};

/**
 * @type {import('./index.d').addFilterListSelectedClass}
 */
export const addFilterListSelectedClass = (context, key, value) => {
  const actionId = simpleMd5(`${key}-${value}`);
  const domElement = context.$(`#${actionId}`, getFilterTreeParent(context));
  domElement && domElement.classList.add(SELECTED_CLASS);
};

/**
 * @type {import('./index.d').removeFilterListSelectedClass}
 */
export const removeFilterListSelectedClass = (context, key, value) => {
  const actionId = simpleMd5(`${key}-${value}`);
  const domElement = context.$(`#${actionId}`, getFilterTreeParent(context));
  domElement && domElement.classList.remove(SELECTED_CLASS);
};

/**
 * @type {import('./index.d').generateFilterDataForList}
 */
export const generateFilterDataForList = (context, key, value, filterOption) => {
  if (['variants_price', 'price'].includes(filterOption.filterType)) {
    const [from, to] = value.split(':');
    filterOption.valueDisplay = formatPriceLabel(context, to, from);
  }

  if (filterOption.filterType === 'percent_sale') {
    const [from, to] = value.split(':');
    filterOption.valueDisplay = formatPercentSaleLabel(context, Number(from) || 0, to);
  }

  return {
    data: {
      label: filterOption.label,
      value,
      key,
      valueDisplay: filterOption.valueDisplay || value,
      selectType: filterOption.selectType,
    },
    metaData: { key, value, type: filterOption.displayType },
    type: filterOption.displayType,
  };
};
